import React, { useEffect, useState } from "react";
import { Container, Grid, Button, Box, Alert, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  findFund,
  findStrategy,
  TABLE_HEAD_FUND_REBALANCE_BLACKLIST,
} from "../../../../../../../component/Methods";
import LocatesNotFoundPopup from "../../../../../Popup/LocatesNotFoundPopup";
import Blacklisted from "./components/Blacklisted";
import { useDispatch, useSelector } from "react-redux";
import {selectActiveRequests} from "../../../../../../../Redux/Reducers/selectors";
import { useRebalancerState } from "../../../../../../../hooks/useOptimizedState";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../../../Redux/Reducers/uiSlice";
import { showComposite } from "../../../../../../../component/Methods/mainFunctions";
import BlacklistedSecuritiesSkeleton from "../../../../../../../component/spinner/BlacklistedSecuritiesSkeleton";
import { sendWebSocketMessage } from "../../../../../../../hooks/useWebSocket";

const FundRebalanceStep4 = ({
  sleeve,
  setCurrentStep,
  rebalanceType,
  rebalancedFunds,
  rebalancedStrategies,
  deltaRebalancer,
  deltaValue,
  driftValue,
}) => {
  const dispatch = useDispatch();
  const { funds, blacklistedTickers, strategies } = useRebalancerState();
  const activeRequests = useSelector(selectActiveRequests); 
  

  const [popupOpen, setPopupOpen] = useState(false);
  const [blacklist, setBlacklistData] = useState([]);
  const [selectedCompositeKey, setSelectedCompositeKey] = useState("all");
  const [CompositeKeys, setCompositeKeys] = useState([]);
  const [strategyData, setStrategyData] = useState([]);
  const [totalBlacklistedTickers, setTotalBlacklistedTickers] = useState(0);
  const [totalTickersChecked, setTotalTickersChecked] = useState(0);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };
  const combineBlacklistedTickers = (tickersArray) => {
    return tickersArray?.reduce((acc, fundObj) => {
      const tickers = Object.entries(
        fundObj?.blacklistedTickers || {}
      )?.flatMap(([compositeKey, tickerData]) => {
        const tickersArray = tickerData?.blacklistedTickers || [];
        // Filter out invalid ticker objects before mapping
        return tickersArray
          ?.filter(tickerObj => tickerObj && typeof tickerObj === 'object' && Object.keys(tickerObj).length > 0)
          ?.map((tickerObj) => ({
            ...tickerObj,
            strategy: compositeKey?.split("#")[0],
            fund: compositeKey?.split("#")[1],
          })) || [];
      });
      return acc?.concat(tickers);
    }, []);
  };

  useEffect(() => {
    if (sleeve && blacklistedTickers && blacklistedTickers.length > 0) {
      const compositeKeys = blacklistedTickers?.flatMap((fundObj) =>
        Object.keys(fundObj?.blacklistedTickers || {})
      );
      const allTickers = combineBlacklistedTickers(blacklistedTickers);
      setCompositeKeys(compositeKeys);
      setSelectedCompositeKey("all");
      setStrategyData(allTickers);

      const totals = blacklistedTickers?.reduce(
        (acc, fundObj) => {
          Object.values(fundObj?.blacklistedTickers || {}).forEach(
            (tickerData) => {
              acc.totalBlacklisted += tickerData?.totalBlacklistedTickers || 0;
              acc.totalChecked += tickerData?.totalTickersChecked || 0;
            }
          );
          return acc;
        },
        { totalBlacklisted: 0, totalChecked: 0 }
      );

      setTotalBlacklistedTickers(totals.totalBlacklisted);
      setTotalTickersChecked(totals.totalChecked);
    } else {
      setStrategyData([]);
      setCompositeKeys([]);
      setTotalBlacklistedTickers(0);
      setTotalTickersChecked(0);
    }
  }, [sleeve, blacklistedTickers]);

  useEffect(() => {
    if (strategyData) {
      const updatedList = strategyData?.map((item) => ({
        reason: item?.reason,
        ticker: item?.ticker?.symbol,
        fund: findFund(item?.fund, funds),
        strategy: findStrategy(item?.strategy, strategies),
        direction: item?.direction,
      }));
      setBlacklistData(updatedList);
    }
  }, [strategyData]);

  const handleCompositeKeyChange = (e) => {
    const newCompositeKey = e.target.value;
    setSelectedCompositeKey(newCompositeKey);

    if (newCompositeKey === "all") {
      const allTickers = combineBlacklistedTickers(blacklistedTickers);
      setStrategyData(allTickers);

      // Calculate totals for "All" selection
      const totals = blacklistedTickers?.reduce(
        (acc, fundObj) => {
          Object.values(fundObj?.blacklistedTickers || {}).forEach(
            (tickerData) => {
              acc.totalBlacklisted += tickerData?.totalBlacklistedTickers || 0;
              acc.totalChecked += tickerData?.totalTickersChecked || 0;
            }
          );
          return acc;
        },
        { totalBlacklisted: 0, totalChecked: 0 }
      );

      setTotalBlacklistedTickers(totals.totalBlacklisted);
      setTotalTickersChecked(totals.totalChecked);
    } else {
      const selectedFund = blacklistedTickers?.find((fundObj) =>
        Object.keys(fundObj?.blacklistedTickers || {})?.includes(
          newCompositeKey
        )
      );

      if (selectedFund) {
        const tickerData = selectedFund?.blacklistedTickers[newCompositeKey];
        const tickers =
          tickerData?.blacklistedTickers
            ?.filter(tickerObj => tickerObj && typeof tickerObj === 'object' && Object.keys(tickerObj).length > 0)
            ?.map((tickerObj) => ({
              ...tickerObj,
              fund: newCompositeKey?.split("#")[1],
              strategy: newCompositeKey?.split("#")[0],
            })) || [];
        setStrategyData(tickers);

        // Calculate totals for specific composite key
        setTotalBlacklistedTickers(tickerData?.totalBlacklistedTickers || 0);
        setTotalTickersChecked(tickerData?.totalTickersChecked || 0);
      }
    }
  };

  const onNext = () => {
    const blacklistedData = rebalancedFunds?.reduce((acc, fundId) => {
      const matchedFund = blacklistedTickers?.find(
        (item) => item?.fundId === fundId
      );

      if (matchedFund && matchedFund?.blacklistedTickers) {
        const aggregatedTickers = Object.values(matchedFund?.blacklistedTickers)
          ?.flatMap((tickerData) => tickerData?.blacklistedTickers || [])
          ?.map(({ ticker, reason, direction }) => ({
            ...ticker,
          }));

        acc[fundId] = aggregatedTickers.length > 0 ? aggregatedTickers : [];
      } else {
        acc[fundId] = [];
      }

      return acc;
    }, {});

    const socketRequest = {
      action: "create_rebalancer_items",
      data: {
        fundIds: rebalancedFunds,
        blacklistedTickers: blacklistedData,
        rebalancerType: rebalanceType === "all" ? 1 : 0,
        deltaRebalancer: Number(deltaRebalancer),
        deltaValue: Number(deltaValue),
        strategies: rebalancedStrategies,
        driftValue: Number(driftValue),
      },
    };
    sendWebSocketMessage(JSON.stringify(socketRequest));
    dispatch(addActiveRequests("create_rebalancer_items"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Creating Rebalancer Items"));
    setCurrentStep((prev) => prev + 1);
  };

  // Check if we're loading blacklisted data
  const isLoadingBlacklistedData = activeRequests.includes(
    "rebalancer_check_blacklist"
  );

  // Show skeleton while loading
  if (isLoadingBlacklistedData) {
    return <BlacklistedSecuritiesSkeleton />;
  }

  return (
    <Container disableGutters maxWidth="100%" sx={{ marginTop: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ width: "40%" }}>
              <Box
                sx={{
                  marginBottom: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: "bold", fontSize: "20px" }}>
                  Blacklisted Securities{totalBlacklistedTickers > 0 ? `: ${totalBlacklistedTickers} / ${totalTickersChecked}` : ''}
                </Typography>
                {sleeve && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <select
                      value={selectedCompositeKey}
                      onChange={handleCompositeKeyChange}
                      style={{
                        padding: "5px",
                        fontSize: "14px",
                        marginBottom: 10,
                      }}
                      size="small"
                    >
                      <option value="all">All</option>
                      {CompositeKeys?.map((compositeKey) => (
                        <option key={compositeKey} value={compositeKey}>
                          {showComposite(compositeKey, strategies, funds)}
                        </option>
                      ))}
                    </select>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                sx={{ width: "150px", height: "40px" }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Alert severity="info">
              The securities that appear below will not be included in final
              rebalancer trades.
            </Alert>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "row", width: "45%" }}>
            {blacklist?.length > 0 ? (
              <>
                <Blacklisted
                  blacklist={blacklist}
                  tableHeader={TABLE_HEAD_FUND_REBALANCE_BLACKLIST}
                  totalBlacklistedTickers={totalBlacklistedTickers}
                  totalTickersChecked={totalTickersChecked}
                />
              </>
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                No blacklisted tickers found
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <LocatesNotFoundPopup open={popupOpen} onClose={handleClosePopup} />
    </Container>
  );
};

export default FundRebalanceStep4;
